import type { TemplateDatePreset } from "./types";
import type { TemplateDateFilterValue } from "../../utils/templateDateFilterTypes";
import { format, subMonths } from "date-fns";
import { enUS } from "date-fns/locale";

function epochSec(d: Date): number {
  return Math.floor(d.getTime() / 1000);
}

function parseYyyyMmDd(s: string): { y: number; m: number; d: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim());
  if (!m) {
    return null;
  }
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!y || mo < 1 || mo > 12 || d < 1 || d > 31) {
    return null;
  }
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) {
    return null;
  }
  return { y, m: mo, d };
}

/** Inclusive local calendar day range → `"startSec - endSec"` for BETWEEN_TS. */
function customLocalDaysToEpochRange(from: string, to: string): string | null {
  const fromTrim = (from ?? "").trim();
  const toTrim = (to ?? "").trim();
  if (!fromTrim && !toTrim) {
    return null;
  }

  if (fromTrim && !toTrim) {
    const b = parseYyyyMmDd(fromTrim);
    if (!b) {
      return null;
    }
    const start = new Date(b.y, b.m - 1, b.d, 0, 0, 0, 0);
    const end = new Date(b.y, b.m - 1, b.d, 23, 59, 59, 999);
    return `${epochSec(start)} - ${epochSec(end)}`;
  }
  if (!fromTrim && toTrim) {
    const b = parseYyyyMmDd(toTrim);
    if (!b) {
      return null;
    }
    const start = new Date(b.y, b.m - 1, b.d, 0, 0, 0, 0);
    const end = new Date(b.y, b.m - 1, b.d, 23, 59, 59, 999);
    return `${epochSec(start)} - ${epochSec(end)}`;
  }
  const a = parseYyyyMmDd(fromTrim);
  const b = parseYyyyMmDd(toTrim);
  if (!a || !b) {
    return null;
  }
  let startD = new Date(a.y, a.m - 1, a.d, 0, 0, 0, 0);
  let endD = new Date(b.y, b.m - 1, b.d, 23, 59, 59, 999);
  if (startD > endD) {
    const t = startD;
    startD = new Date(endD.getFullYear(), endD.getMonth(), endD.getDate(), 0, 0, 0, 0);
    endD = new Date(t.getFullYear(), t.getMonth(), t.getDate(), 23, 59, 59, 999);
  }
  return `${epochSec(startD)} - ${epochSec(endD)}`;
}

export type BetweenTsClause = {
  value: string;
  comparisonType: "BETWEEN_TS";
};

export type TemplateCreatedDateFilter = {
  createdDate: BetweenTsClause;
};

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Default template date filter: inclusive local range from 6 months ago through today. */
export function createDefaultTemplateDateFilterLast6Months(): TemplateDateFilterValue {
  const today = startOfLocalDay(new Date());
  const from = startOfLocalDay(subMonths(today, 6));
  return {
    templateDatePreset: "custom",
    templateDateFrom: format(from, "yyyy-MM-dd"),
    templateDateTo: format(today, "yyyy-MM-dd"),
  };
}

const TEMPLATE_DATE_PRESETS: ReadonlySet<string> = new Set([
  "all",
  "today",
  "last-3-days",
  "last-7-days",
  "last-week",
  "last-month",
  "current-year",
  "year-2025",
  "year-2024",
  "year-2023",
  "year-2022",
  "custom",
]);

/** Restore a previously persisted Send Email / catalog date filter, or null if invalid. */
export function parseStoredTemplateDateFilter(
  value: unknown,
): TemplateDateFilterValue | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const record = value as Record<string, unknown>;
  const preset = String(record.templateDatePreset ?? "").trim();
  if (!TEMPLATE_DATE_PRESETS.has(preset)) {
    return null;
  }
  return {
    templateDatePreset: preset as TemplateDateFilterValue["templateDatePreset"],
    templateDateFrom: String(record.templateDateFrom ?? "").trim(),
    templateDateTo: String(record.templateDateTo ?? "").trim(),
  };
}

/** Monday 00:00 local of the calendar week containing `d` (ISO-style week, Mon–Sun). */
function startOfWeekMonday(d: Date): Date {
  const s = startOfLocalDay(d);
  const dow = s.getDay();
  const offset = dow === 0 ? -6 : 1 - dow;
  s.setDate(s.getDate() + offset);
  return s;
}

function localInclusiveRangeToEpoch(from: Date, to: Date): string {
  const a = startOfLocalDay(from);
  const b = startOfLocalDay(to);
  const start = new Date(a.getFullYear(), a.getMonth(), a.getDate(), 0, 0, 0, 0);
  const end = new Date(b.getFullYear(), b.getMonth(), b.getDate(), 23, 59, 59, 999);
  return `${epochSec(start)} - ${epochSec(end)}`;
}

function fullCalendarYearLocalRange(year: number): { from: Date; to: Date } {
  return {
    from: startOfLocalDay(new Date(year, 0, 1)),
    to: startOfLocalDay(new Date(year, 11, 31)),
  };
}

function currentYearToDateLocalRange(): { from: Date; to: Date } {
  const today = startOfLocalDay(new Date());
  return {
    from: startOfLocalDay(new Date(today.getFullYear(), 0, 1)),
    to: today,
  };
}

/**
 * Local calendar range for a named preset (for UI / DayPicker).
 * Mirrors {@link buildTemplateCreatedDateFilter} semantics.
 */
export function getPresetLocalDateRange(
  preset: Exclude<TemplateDatePreset, "custom" | "all">,
): { from: Date; to: Date } {
  const today = startOfLocalDay(new Date());

  switch (preset) {
    case "today":
      return { from: today, to: today };
    case "last-3-days": {
      const from = new Date(today);
      from.setDate(from.getDate() - 2);
      return { from: startOfLocalDay(from), to: today };
    }
    case "last-7-days": {
      const from = new Date(today);
      from.setDate(from.getDate() - 6);
      return { from: startOfLocalDay(from), to: today };
    }
    case "last-week": {
      const thisMonday = startOfWeekMonday(today);
      const lastMonday = new Date(thisMonday);
      lastMonday.setDate(lastMonday.getDate() - 7);
      const lastSunday = new Date(lastMonday);
      lastSunday.setDate(lastSunday.getDate() + 6);
      return { from: lastMonday, to: startOfLocalDay(lastSunday) };
    }
    case "last-month": {
      const firstThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastPrev = new Date(firstThisMonth);
      lastPrev.setDate(0);
      const firstPrev = new Date(lastPrev.getFullYear(), lastPrev.getMonth(), 1);
      return {
        from: startOfLocalDay(firstPrev),
        to: startOfLocalDay(lastPrev),
      };
    }
    case "current-year":
      return currentYearToDateLocalRange();
    case "year-2025":
      return fullCalendarYearLocalRange(2025);
    case "year-2024":
      return fullCalendarYearLocalRange(2024);
    case "year-2023":
      return fullCalendarYearLocalRange(2023);
    case "year-2022":
      return fullCalendarYearLocalRange(2022);
  }
}

/** Shared BETWEEN_TS `{ value: "unixStart - unixEnd" }` for numeric epoch fields stored in Cosmos. */
export function buildBetweenTsPresetFilterClause(
  preset: TemplateDatePreset,
  dateFrom: string,
  dateTo: string,
): BetweenTsClause | undefined {
  if (preset === "all") {
    return undefined;
  }

  if (preset === "custom") {
    const range = customLocalDaysToEpochRange(dateFrom, dateTo);
    if (!range) {
      return undefined;
    }
    return {
      value: range,
      comparisonType: "BETWEEN_TS",
    };
  }

  const r = getPresetLocalDateRange(preset);
  return {
    value: localInclusiveRangeToEpoch(r.from, r.to),
    comparisonType: "BETWEEN_TS",
  };
}

function formatDisplayMdY(d: Date): string {
  return format(d, "MMM d, yyyy", { locale: enUS });
}

/**
 * Human-readable range for filter chips / summaries (matches {@link TemplateDateFlightsPicker} trigger text).
 */
export function summarizeTemplateDateFilterSelection(
  value: TemplateDateFilterValue | null | undefined,
): string | null {
  if (value == null || value.templateDatePreset === "all") {
    return null;
  }

  if (value.templateDatePreset !== "custom") {
    const r = getPresetLocalDateRange(
      value.templateDatePreset as Exclude<TemplateDatePreset, "custom" | "all">,
    );
    const fromLbl = formatDisplayMdY(startOfLocalDay(r.from));
    const toLbl = formatDisplayMdY(startOfLocalDay(r.to));
    return fromLbl === toLbl ? fromLbl : `${fromLbl} – ${toLbl}`;
  }

  const fromTrim = (value.templateDateFrom ?? "").trim();
  const toTrim = (value.templateDateTo ?? "").trim();
  if (!fromTrim && !toTrim) {
    return null;
  }

  if (fromTrim && !toTrim) {
    const b = parseYyyyMmDd(fromTrim);
    if (!b) {
      return null;
    }
    return formatDisplayMdY(new Date(b.y, b.m - 1, b.d));
  }
  if (!fromTrim && toTrim) {
    const b = parseYyyyMmDd(toTrim);
    if (!b) {
      return null;
    }
    return formatDisplayMdY(new Date(b.y, b.m - 1, b.d));
  }
  const a = parseYyyyMmDd(fromTrim);
  const b = parseYyyyMmDd(toTrim);
  if (!a || !b) {
    return null;
  }
  let startD = new Date(a.y, a.m - 1, a.d);
  let endD = new Date(b.y, b.m - 1, b.d);
  if (startD > endD) {
    const t = startD;
    startD = endD;
    endD = t;
  }
  const fromLbl = formatDisplayMdY(startD);
  const toLbl = formatDisplayMdY(endD);
  return fromLbl === toLbl ? fromLbl : `${fromLbl} – ${toLbl}`;
}

/**
 * Builds the `createdDate` Cosmos filter for email templates (same contract as legacy “By Year”).
 */
export function buildTemplateCreatedDateFilter(
  preset: TemplateDatePreset,
  dateFrom: string,
  dateTo: string,
): TemplateCreatedDateFilter | undefined {
  const clause = buildBetweenTsPresetFilterClause(preset, dateFrom, dateTo);
  if (!clause) {
    return undefined;
  }
  return { createdDate: clause };
}
